// Auth-related helpers. Keeps every consumer from poking localStorage
// directly so we can change the storage strategy (cookies, etc.) in one
// place later.

// `refreshToken` lives in localStorage like the access token. Same XSS surface,
// but the win is duration: a stolen access JWT is good for at most ACCESS_TOKEN_
// EXPIRY_MINUTES (down to 15 once the SPA is on the new flow); a stolen refresh
// token is invalidated on the next legitimate refresh via rotation (the
// reuse-detection path on the server logs out the entire session). Moving the
// refresh token to a HttpOnly cookie would be more secure but changes the auth
// model significantly — separate decision later.
const AUTH_KEYS = ["token", "tokenType", "currentUser", "encodedToken", "refreshToken"];

/**
 * Clear only auth-related keys from localStorage. Preserves user
 * preferences such as saved filters, search inputs, and dashboard
 * selections so they survive logout / session-expiry redirects.
 */
export function clearAuth() {
  try {
    AUTH_KEYS.forEach((key) => localStorage.removeItem(key));
  } catch (e) {
    // localStorage can be blocked in some browsing modes — ignore.
  }
}

/**
 * Safe parse of the stored current user. Returns an empty object when
 * the value is missing or corrupted.
 */
export function getCurrentUser() {
  try {
    return JSON.parse(localStorage.getItem("currentUser")) || {};
  } catch (e) {
    return {};
  }
}

/**
 * Read the raw bearer token. Returns null when missing so apiHelper can
 * tell whether to inject the Authorization header.
 */
export function getToken() {
  try {
    return localStorage.getItem("token") || null;
  } catch (e) {
    return null;
  }
}

/**
 * Read the stored refresh token. Returns null when missing; apiHelper's 401
 * interceptor uses null to decide between "refresh the session" and "drop
 * straight to the login page".
 */
export function getRefreshToken() {
  try {
    return localStorage.getItem("refreshToken") || null;
  } catch (e) {
    return null;
  }
}

/**
 * Persist a fresh token pair after login or refresh. Centralised so the schema
 * change (e.g. moving refreshToken to a cookie later) is a one-file edit.
 */
export function storeTokens({ token, tokenType, refreshToken }) {
  try {
    if (token) localStorage.setItem("token", token);
    if (tokenType) localStorage.setItem("tokenType", tokenType);
    if (token) localStorage.setItem("encodedToken", token);
    if (refreshToken) localStorage.setItem("refreshToken", refreshToken);
  } catch (e) {
    // localStorage can be blocked — caller can detect via getToken().
  }
}

/**
 * Parse a JSON-encoded localStorage value. Returns null when the key is
 * missing OR the stored value is unparseable, so callers never have to
 * wrap reads in try/catch and a corrupted entry can't crash a route.
 */
export function safeParse(key) {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return null;
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
}
